import {
    FormControl,
    FormControlLabel
} from '@mui/material'
import { ButtonForm } from 'Components/Form/styles'
import { Column, Row } from 'Components/Grid'
import { Checkbox, Select, TextField } from 'Components/ui'
import { MuiChipsInput } from 'mui-chips-input'
import { Controller, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useCountryConfig } from 'hooks/useCountryConfig'
import { validateTaxId, stripTaxIdFormatting } from 'utils/tax_id_validators'
import { useTranslation } from 'react-i18next'

export const Detalhes = ({
    handleNext,
    register,
    control,
    handleSubmit,
    errors,
    getValues
}) => {
    const { t } = useTranslation(['company'])
    const navigate = useNavigate()
    const countryValue = useWatch({ control, name: 'country' })
    const { taxIdLabel, taxIdMask, taxIdPlaceholder, countryConfig, allConfigs, isError: isCountryConfigError, isLoading: isCountryConfigLoading } = useCountryConfig(countryValue)
    const onSubmit = () => {
        handleNext()
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('name', { required: t('company:companies.form.nameRequired') })}
                        label={t('company:companies.form.name')}
                        autoFocus
                        error={errors?.name ? true : false}
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        helperText={errors?.name ? errors.name.message : null}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('corporateName', { required: t('company:companies.form.corporateNameRequired') })}
                        label={t('company:companies.form.corporateName')}
                        autoFocus
                        error={errors?.corporateName ? true : false}
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        helperText={errors?.corporateName ? errors.corporateName.message : null}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('slug', { required: t('company:companies.form.slugRequired') })}
                        label={t('company:companies.form.slug')}
                        error={errors?.slug ? true : false}
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        helperText={errors?.slug ? errors.slug.message : null}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <Controller
                        control={control}
                        name="country"
                        rules={{ required: t('company:companies.form.countryRequired') }}
                        render={({ field: { onChange, value } }) => (
                            <Select
                                name="country"
                                label={t('company:companies.form.country')}
                                value={value}
                                onChange={onChange}
                                disabled={isCountryConfigError}
                                error={!!errors?.country || isCountryConfigError}
                                helperText={isCountryConfigError
                                    ? t('company:companies.form.countryErrorHelper')
                                    : (errors?.country ? errors.country.message : '')}
                                sx={{ marginBottom: '24px' }}
                                options={[
                                    { value: '', label: isCountryConfigError ? t('company:companies.form.countryError') : t('company:companies.form.countryPlaceholder') },
                                    ...allConfigs.map(cc => ({ value: cc.countryCode, label: cc.name.pt })),
                                ]}
                            />
                        )}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <Controller
                        control={control}
                        name="defaultLanguage"
                        render={({ field: { onChange, value } }) => (
                            <Select
                                name="defaultLanguage"
                                value={value}
                                onChange={onChange}
                                error={!!errors?.defaultLanguage}
                                helperText={errors?.defaultLanguage ? errors.defaultLanguage.message : ''}
                                sx={{ marginBottom: '24px' }}
                                options={[
                                    { value: '', label: t('company:companies.form.languagePlaceholder') },
                                    { value: 'pt', label: t('company:companies.form.languagePt') },
                                    { value: 'en', label: t('company:companies.form.languageEn') },
                                    { value: 'es', label: t('company:companies.form.languageEs') },
                                ]}
                            />
                        )}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('cnpj', {
                            required: `${taxIdLabel || 'Tax ID'} é obrigatório`,
                            validate: (value) => {
                                if (!value || !countryValue) return true;
                                if (isCountryConfigLoading) return 'Aguardando configuração de país...';
                                if (!countryConfig) return true;
                                const stripped = stripTaxIdFormatting(value);
                                // Only run format regex when value has visual separators (formatted input)
                                // Skip for already-stripped DB values on edit to avoid false failures
                                if (stripped !== value && countryConfig.taxId?.regex) {
                                    const formatOk = new RegExp(countryConfig.taxId.regex).test(value);
                                    if (!formatOk) return `Formato inválido. Use: ${taxIdMask}`;
                                }
                                const checkOk = validateTaxId(stripped, countryValue);
                                if (!checkOk) return `${taxIdLabel} inválido. Use o formato: ${taxIdMask}`;
                                return true;
                            },
                        })}
                        label={taxIdLabel || 'CNPJ'}
                        placeholder={taxIdPlaceholder}
                        error={errors?.cnpj ? true : false}
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        helperText={errors?.cnpj ? errors.cnpj.message : null}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('size')}
                        label={t('company:companies.form.size')}
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('income')}
                        label={t('company:companies.form.income')}
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <TextField
                        {...register('industry')}
                        label={t('company:companies.form.industry')}
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="center">
                <Column xs={12} className="d-flex">
                    <FormControl
                        fullWidth
                        sx={{ margin: '0 auto 24px' }}
                    >
                        <Controller
                            control={control}
                            name="domains"
                            render={({ field: { onChange, value }}) => (
                                <MuiChipsInput
                                label={t('company:companies.form.domains')}
                                value={value}
                                onChange={onChange}
                                InputLabelProps={{ shrink: true }}
                                placeholder={t('company:companies.form.domainsPlaceholder')}
                                renderChip={(Component, props) => {
                                    return (
                                    <Component
                                        {...props}
                                        variant="outlined"
                                        color="primary"
                                        sx={{
                                        width: 'fit-content',
                                        }}
                                    />
                                    )
                                }}
                                sx={{
                                    '& .MuiButtonBase-root.MuiChip-root.MuiChip-filled': {
                                    width: 'fit-content'
                                    },
                                    '& .MuiButtonBase-root.MuiIconButton-root': {
                                    width: 'fit-content',
                                    marginTop: '0px'
                                    }
                                }}/>
                            )}
                        ></Controller>
                    </FormControl>
                </Column>
            </Row>
            <Row>
                <Column xs={12} className="d-flex" style={{gap: '24px', flexWrap: 'wrap'}}>
                    <Controller
                        control={control}
                        name="active"
                        render={({ field: { onChange, value }}) => (
                            <FormControlLabel
                                value={value}
                                control={<Checkbox checked={value} onChange={onChange} name="active" />}
                                label={t('company:companies.form.active')}
                                sx={{marginLeft: 0, marginBottom: '16px'}}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="partner"
                        render={({ field: { onChange, value }}) => (
                            <FormControlLabel
                                value={value}
                                control={<Checkbox checked={value} onChange={onChange} name="partner" />}
                                label={t('company:companies.form.partner')}
                                sx={{marginLeft: 0, marginBottom: '16px'}}
                            />
                        )}
                    />
                </Column>
            </Row>
            <Row>
                <Column xs={12} className="d-flex">
                    <Controller
                        control={control}
                        rules={{
                        validate: () => {
                            return getValues('signedPlan') !== ''
                        },
                        required: t('company:companies.form.signedPlanRequired'),
                        }}
                        name="signedPlan"
                        render={({ field: { onChange, value } }) => (
                        <Select
                            name="signedPlan"
                            value={value}
                            onChange={onChange}
                            error={errors?.signedPlan ? true : false}
                            helperText={errors?.signedPlan ? errors.signedPlan.message : ''}
                            sx={{ marginBottom: '24px' }}
                            options={[
                                { value: '', label: t('company:companies.form.signedPlanPlaceholder') },
                                ...['Free', 'Starter', 'Premium'].map((item) => ({ value: item, label: item })),
                            ]}
                        />
                        )}
                    />
                </Column>
            </Row>
            <Row>
                <Column>
                    <TextField
                    {...register('description')}
                    label={t('company:companies.form.description')}
                    multiline
                    rows={4}
                    sx={{ margin: '0 auto 24px', width: '100%' }}
                    InputLabelProps={{ shrink: true }}
                    />
                </Column>
            </Row>
            <Row justifyContent="end" alignItems="center">
                <Column xs={4} md={3} className="d-flex" style={{ gap: '8px', justifyContent: 'flex-end' }}>
                    <ButtonForm
                        variant="secondary"
                        id="cancel"
                        onClick={() => navigate('/companies')}
                    >
                        {t('company:companies.form.cancel')}
                    </ButtonForm>
                    <ButtonForm type="submit">{t('company:companies.form.next')}</ButtonForm>
                </Column>
            </Row>
        </form>
    )
}
